import React, {useMemo} from 'react';
import {Card, Form} from 'react-bootstrap';
import uniqueId from 'lodash/uniqueId';

import css from './WidgetPreviewContainer.module.css';
import {
  MetaAlanWakeWidget,
  MetaSpeedtestWidget,
  MetaValorantWidget,
  MetaAloneInTheDarkWidget,
  MetaFalloutWidget,
  MetaStarWarsWidget,
  MetaValorantWidgetV2,
  MetaEldenRingWidget,
  MetaPulsatingWidget,
} from '@pulsoid-private/widgets-lib';
import classNames from 'classnames';

export type WidgetPreviewContainerProps = {
  id?: string;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  darkByDefault?: boolean;
  header?: React.ReactNode;
  actions?: React.ReactNode;
};

const DARK_BY_DEFAULT_WIDGETS = new Set([
  MetaValorantWidget,
  MetaSpeedtestWidget,
  MetaAlanWakeWidget,
  MetaAloneInTheDarkWidget,
  MetaFalloutWidget,
  MetaStarWarsWidget,
  MetaValorantWidgetV2,
  MetaEldenRingWidget,
  MetaPulsatingWidget,
]);

export const WidgetPreviewContainer: React.FC<WidgetPreviewContainerProps> = ({
  children,
  title = '',
  subtitle,
  header,
  id,
  darkByDefault = DARK_BY_DEFAULT_WIDGETS.has(id as string),
  actions,
}) => {
  const [darkMode, setDarkMode] = React.useState(darkByDefault);
  const darkModeSwitchId = useMemo(() => uniqueId('dark-mode-switch-'), []);

  return (
    <Card className={classNames(css.root, {[css.withHeader]: !!header})}>
      {header ? <Card.Header>{header}</Card.Header> : null}
      <Card.Body
        className={css.content}
        style={{
          backgroundColor: darkMode ? '#333333' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
        }}
      >
        <div className={css.modeSwitch}>
          <Form.Label
            htmlFor={darkModeSwitchId}
            className={css.modeSwitchLabel}
          >
            Light
          </Form.Label>
          <Form.Switch
            className={css.modeSwitchControl}
            id={darkModeSwitchId}
            defaultChecked={darkByDefault}
            onChange={(evt) =>
              setDarkMode((evt?.target as HTMLInputElement)?.checked)
            }
          />
          <Form.Label
            htmlFor={darkModeSwitchId}
            className={css.modeSwitchLabel}
          >
            Dark
          </Form.Label>
        </div>

        <Card.Title className={css.title}>{title}</Card.Title>
        {subtitle ? (
          <Card.Subtitle className={css.subtitle} style={{fontSize: 10}}>
            {subtitle}
          </Card.Subtitle>
        ) : null}

        <div className={css.widgetContainer}>
          <div
            className={classNames(css.widget, {
              [css.widgetNoActions]: !actions,
            })}
          >
            {children}
          </div>
        </div>

        {actions ? actions : null}
      </Card.Body>
    </Card>
  );
};
